import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ImageOff, CalendarDays } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { pageTitle, truncate } from "@/lib/seo";
import { useSiteContent, scValue } from "@/hooks/useSiteContent";
import { blogImage, blogLang, formatBlogDate } from "@/lib/blog";

const BlogCard = ({ post, lang, catName, index = 0 }) => {
  const img = blogImage(post.image_url);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}>
      <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}
        className="group flex flex-col h-full bg-white rounded-2xl border border-brand-navy/10 overflow-hidden lift">
        <div className="aspect-[16/9] bg-brand-light overflow-hidden">
          {img ? <img src={img} alt={blogLang(post, "title", lang)} loading="lazy" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-brand-navy/25"><ImageOff className="h-10 w-10" /></div>}
        </div>
        <div className="p-5 flex flex-col flex-1">
          {catName && <span className="text-xs font-semibold uppercase tracking-wider text-brand-electric">{catName}</span>}
          <h3 className="mt-2 font-heading font-semibold text-lg text-brand-navy leading-snug line-clamp-2">{blogLang(post, "title", lang)}</h3>
          <p className="mt-2 text-sm text-brand-navy/60 line-clamp-3 flex-1">{blogLang(post, "excerpt", lang)}</p>
          <div className="mt-4 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-xs text-brand-navy/45"><CalendarDays className="h-3.5 w-3.5" /> {formatBlogDate(post.published_at, lang)}</span>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-electric group-hover:text-brand-orange transition-colors">
              {lang === "en" ? "Read article" : "Leggi l'articolo"} <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const Blog = () => {
  const { t, lang } = useI18n();
  const sc = useSiteContent();
  const pb = sc?.page_blog;
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("");

  useEffect(() => {
    api.get("/blog/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/blog/posts", { params: active ? { category: active } : {} }).then(({ data }) => setPosts(data)).catch(() => {});
  }, [active]);

  const catName = (slug) => { const c = cats.find((x) => x.slug === slug); return c ? (lang === "en" ? c.name_en : c.name_it) : ""; };
  const title = scValue(pb, "title", lang, "Blog");
  const subtitle = scValue(pb, "subtitle", lang, "");

  return (
    <div data-testid="blog-page">
      <Seo
        title={pageTitle(scValue(pb, "seoTitle", lang, "") || title)}
        description={truncate(scValue(pb, "seoDesc", lang, "") || subtitle)}
        path="/blog"
        type="website"
      />
      <section className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading tracking-tight">{title}</h1>
          {subtitle && <p className="mt-4 text-white/70 max-w-2xl">{subtitle}</p>}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        {cats.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-category-filter">
            <button data-testid="blog-cat-all" onClick={() => setActive("")}
              className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${active === "" ? "bg-brand-electric text-white border-brand-electric" : "border-brand-navy/15 text-brand-navy/70 hover:border-brand-electric"}`}>
              {lang === "en" ? "All" : "Tutti"}
            </button>
            {cats.map((c) => (
              <button key={c.slug} data-testid={`blog-cat-${c.slug}`} onClick={() => setActive(c.slug)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${active === c.slug ? "bg-brand-electric text-white border-brand-electric" : "border-brand-navy/15 text-brand-navy/70 hover:border-brand-electric"}`}>
                {lang === "en" ? c.name_en : c.name_it}
              </button>
            ))}
          </div>
        )}
        {posts.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => <BlogCard key={p.id} post={p} lang={lang} catName={catName(p.category_slug)} index={i} />)}
          </div>
        ) : (
          <p className="text-center text-brand-navy/40 py-16" data-testid="blog-empty">{lang === "en" ? "No articles yet." : "Nessun articolo al momento."}</p>
        )}
      </div>
    </div>
  );
};

export default Blog;
